import { createContext, useContext, type FC, type ReactNode } from 'react';
import type { ChatMessage, Conversation } from '../types/chat.types';
import type { PredefinedPromptProps } from '../types/predefinedPrompts';

/**
 * Stable action functions context - these functions never change reference.
 *
 * This context is designed for performance optimization:
 * - Contains only stable function references that don't change
 * - Functions are wrapped with useCallback to maintain referential equality
 * - Consumed by: ChatInput, History button, Retry button, etc.
 * - Components using this context won't re-render due to function reference changes
 */
interface ChatActionsContextValue {
  /**
   * Starts a new conversation with the given message.
   * Triggers streaming response from the assistant.
   */
  startConversation: (
    message: string,
    predefinedPromptProps?: PredefinedPromptProps,
  ) => Promise<void>;

  /**
   * Adds a new message to the current conversation.
   * If user message, triggers streaming assistant response.
   */
  addMessage: (
    message: Omit<ChatMessage, 'id'>,
    predefinedPromptProps?: PredefinedPromptProps,
  ) => Promise<void>;

  /**
   * Clears the entire conversation and resets to empty state.
   * Removes all messages and conversation history.
   */
  clearConversation: () => void;

  /**
   * Loads a historical conversation into the active chat view.
   */
  loadConversation: (
    conversationId: string,
    conversation: Conversation,
  ) => void;

  /**
   * Loads a historical conversation by ID into the active chat view.
   */
  loadConversationFromHistory: (conversationId: string) => Promise<void>;

  /**
   * Retry the last assistant response (if available).
   * Resends the last user message to generate a new response.
   */
  retry: () => Promise<void>;

  /**
   * Abort the current streaming operation (if active).
   * Stops any in-progress streaming and cleans up resources.
   */
  abort: () => void;
}

const ChatActionsContext = createContext<ChatActionsContextValue | undefined>(
  undefined,
);

interface ChatActionsProviderProps {
  children: ReactNode;
  startConversation: (
    message: string,
    predefinedPromptProps?: PredefinedPromptProps,
  ) => Promise<void>;
  addMessage: (
    message: Omit<ChatMessage, 'id'>,
    predefinedPromptProps?: PredefinedPromptProps,
  ) => Promise<void>;
  clearConversation: () => void;
  loadConversation: (
    conversationId: string,
    conversation: Conversation,
  ) => void;
  loadConversationFromHistory: (conversationId: string) => Promise<void>;
  retry: () => Promise<void>;
  abort: () => void;
}

/**
 * Provider for stable action functions.
 * This context provides action functions that maintain referential equality
 * across renders to prevent unnecessary re-renders in consuming components.
 *
 * All functions passed to this provider should be wrapped with useCallback
 * to ensure stable references.
 */
export const ChatActionsProvider: FC<ChatActionsProviderProps> = ({
  children,
  startConversation,
  addMessage,
  clearConversation,
  loadConversation,
  loadConversationFromHistory,
  retry,
  abort,
}) => {
  const contextValue: ChatActionsContextValue = {
    startConversation,
    addMessage,
    clearConversation,
    loadConversation,
    loadConversationFromHistory,
    retry,
    abort,
  };

  return (
    <ChatActionsContext.Provider value={contextValue}>
      {children}
    </ChatActionsContext.Provider>
  );
};

/**
 * Hook to access stable action functions.
 * Components using this hook will not re-render due to function reference changes
 * as long as the functions are properly memoized with useCallback.
 */
export const useChatActions = (): ChatActionsContextValue => {
  const context = useContext(ChatActionsContext);
  if (context === undefined) {
    throw new Error('useChatActions must be used within a ChatActionsProvider');
  }
  return context;
};
